import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Plus, Trash2, ArrowLeft, Image as ImageIcon, X, Loader2, Gift, Coins, ShieldCheck, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import BankSelector from "./BankSelector";
import { OP_TYPES } from "../lib/opTypes";

const OPERATION_TYPES = OP_TYPES.map((t) => ({ ...t, icon: t.Icon }));

import { todayBR } from "../lib/dates";

function today() {
  return todayBR();
}

export default function EntryForm({ open, onOpenChange, onSaved, editing }) {
  const [step, setStep] = useState("type"); // "type" | "form"
  const [operationType, setOperationType] = useState(null);
  const [date, setDate] = useState(today());
  const [bookmakers, setBookmakers] = useState([{ name: "", stake: "", is_365: false }]);
  const [principalCount, setPrincipalCount] = useState(1);
  const [revenue, setRevenue] = useState("");
  const [notes, setNotes] = useState("");
  const [boostPercent, setBoostPercent] = useState("");
  const [shareCode, setShareCode] = useState("");
  const [importingCode, setImportingCode] = useState(false);
  const [freebetValue, setFreebetValue] = useState("");
  const [match, setMatch] = useState("");
  const [homeTeam, setHomeTeam] = useState("");
  const [awayTeam, setAwayTeam] = useState("");
  const [teamOptions, setTeamOptions] = useState([]);
  const [matchOptions, setMatchOptions] = useState([]);
  const [bankOptions, setBankOptions] = useState([]);
  const [earlyPayout, setEarlyPayout] = useState(false);
  const [paType, setPaType] = useState("ambos"); // "ambos" | "casa" | "fora"
  const [weekly365, setWeekly365] = useState(false);
  const [ticketImageUrl, setTicketImageUrl] = useState(null);
  const [uploadingTicket, setUploadingTicket] = useState(false);
  // Procedimento de Freebet
  const [freebetSubMode, setFreebetSubMode] = useState("guaranteed"); // "guaranteed" | "profit_guaranteed"
  const [revenueDefense, setRevenueDefense] = useState("");
  const [saving, setSaving] = useState(false);

  const isEditing = !!editing;

  const supportsTicketUpload =
    operationType === "surebet" ||
    operationType === "aumentada" ||
    operationType === "extracao_freebet" ||
    operationType === "proc_freebet";

  const handleTicketFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use imagem JPG, PNG ou WebP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx 5MB)");
      return;
    }
    setUploadingTicket(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/ticket", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTicketImageUrl(res.data.url);
      toast.success("Imagem anexada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no upload");
    } finally {
      setUploadingTicket(false);
    }
  };

  const applyEntry = (e) => {
    setStep("form");
    setOperationType(e.operation_type);
    setDate(e.date);
    setBookmakers((e.bookmakers || []).map((b) => ({ name: b.name, stake: String(b.stake), is_365: !!b.is_365 })));
    setPrincipalCount(Math.max(1, Math.min(e.principal_count || 1, 5)));
    setRevenue(e.revenue != null ? String(e.revenue) : "");
    setNotes(e.notes || "");
    setBoostPercent(e.boost_percent != null ? String(e.boost_percent) : "");
    setFreebetValue(e.freebet_value != null ? String(e.freebet_value) : "");
    setMatch(e.match || "");
    setHomeTeam(e.home_team || "");
    setAwayTeam(e.away_team || "");
    setEarlyPayout(!!e.early_payout);
    setPaType(e.pa_type || "ambos");
    setWeekly365(!!e.weekly_365);
    setTicketImageUrl(e.ticket_image_url || null);
    setFreebetSubMode(e.freebet_sub_mode || "guaranteed");
    setRevenueDefense(e.revenue_defense != null ? String(e.revenue_defense) : "");
  };

  const importShareCode = async () => {
    const code = shareCode.trim().toUpperCase();
    if (!code) return;
    setImportingCode(true);
    try {
      const r = await api.get(`/share/${code}`);
      applyEntry(r.data.data);
      toast.success(`Operação importada${r.data.shared_by ? ` de ${r.data.shared_by}` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido");
    } finally {
      setImportingCode(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    // Load previously used matches for autocomplete
    api.get("/matches").then((r) => {
      setMatchOptions(r.data.matches || []);
      setTeamOptions(r.data.all_teams || []);
    }).catch(() => {});
    // Load registered banks for bookmaker autocomplete
    api.get("/banks").then((r) => setBankOptions(r.data.banks || [])).catch(() => {});
    if (editing) {
      applyEntry(editing);
    } else {
      setStep("type");
      setOperationType(null);
      setDate(today());
      setBookmakers([{ name: "", stake: "", is_365: false }]);
      setPrincipalCount(1);
      setRevenue("");
      setNotes("");
      setBoostPercent("");
      setFreebetValue("");
      setMatch("");
      setHomeTeam("");
      setAwayTeam("");
      setEarlyPayout(false);
      setPaType("ambos");
      setWeekly365(false);
      setTicketImageUrl(null);
      setFreebetSubMode("guaranteed");
      setRevenueDefense("");
      setShareCode("");
    }
  }, [open, editing]);

  const totalStake = useMemo(
    () => bookmakers.reduce((acc, b) => acc + (parseFloat(b.stake) || 0), 0),
    [bookmakers]
  );
  const defenseStake = useMemo(
    () => bookmakers.slice(principalCount).reduce((acc, b) => acc + (parseFloat(b.stake) || 0), 0),
    [bookmakers, principalCount]
  );
  const freebetFloat = parseFloat(freebetValue) || 0;
  const isExtracao = operationType === "extracao_freebet";
  const isGiros = operationType === "giros";
  const isProcFreebet = operationType === "proc_freebet";
  const isProcLucro = isProcFreebet && freebetSubMode === "profit_guaranteed";
  const isFreebetType = operationType === "freebet" || isExtracao || isProcFreebet;
  const effectiveStake = isGiros
    ? 0
    : isExtracao
    ? defenseStake
    : isProcFreebet
    ? totalStake
    : operationType === "freebet"
    ? Math.max(0, totalStake - freebetFloat)
    : totalStake;
  const revenueDefenseNum = parseFloat(revenueDefense) || 0;
  const profitMainScenario = (parseFloat(revenue) || 0) - totalStake;
  const profitDefenseScenario = revenueDefenseNum - totalStake;
  const profit = useMemo(() => {
    if (isProcLucro) {
      // Worst case (guaranteed floor)
      return Math.min(profitMainScenario, profitDefenseScenario);
    }
    return (parseFloat(revenue) || 0) - effectiveStake;
  }, [revenue, effectiveStake, isProcLucro, profitMainScenario, profitDefenseScenario]);

  const selectedType = OPERATION_TYPES.find((t) => t.key === operationType);

  const addBookmaker = () => {
    if (bookmakers.length >= 10) return;
    setBookmakers([...bookmakers, { name: "", stake: "", is_365: false }]);
  };
  const removeBookmaker = (idx) => {
    if (bookmakers.length === 1) return;
    // Removing a principal reduces principalCount if it would exceed remaining bookmakers
    setBookmakers(bookmakers.filter((_, i) => i !== idx));
    if (idx < principalCount && principalCount > 1) {
      setPrincipalCount((c) => Math.max(1, c - 1));
    }
  };
  const updateBookmaker = (idx, field, val) => {
    setBookmakers((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: val } : b)));
  };
  const changePrincipalCount = (n) => {
    const nn = Math.max(1, Math.min(5, n));
    setPrincipalCount(nn);
    // Ensure we have at least nn bookmaker slots
    setBookmakers((prev) => {
      if (prev.length >= nn) return prev;
      const extra = Array.from({ length: nn - prev.length }, () => ({ name: "", stake: "", is_365: false }));
      return [...prev, ...extra];
    });
  };

  const handleSubmit = async () => {
    if (!operationType) return;
    let normalized;
    if (isGiros) {
      // Giros — single bookmaker, no stake in cash.
      const b0 = bookmakers[0] || {};
      if (!b0.name || !b0.name.trim()) {
        toast.error("Selecione a casa dos giros");
        return;
      }
      normalized = [{ name: b0.name.trim(), stake: 0, is_365: !!b0.is_365 }];
    } else if (isExtracao) {
      // Principals use freebets (stake forced to 0), require at least one principal name.
      const principals = bookmakers.slice(0, principalCount).map((b) => ({
        name: b.name.trim(),
        stake: 0,
        is_365: !!b.is_365,
      }));
      const missingPrincipal = principals.some((p) => !p.name);
      if (missingPrincipal) {
        toast.error("Informe o nome de todas as casas principais");
        return;
      }
      const defenses = bookmakers
        .slice(principalCount)
        .filter((b) => b.name.trim() && parseFloat(b.stake) > 0)
        .map((b) => ({ name: b.name.trim(), stake: parseFloat(b.stake), is_365: !!b.is_365 }));
      if (defenses.length < 1) {
        toast.error("Adicione ao menos uma defesa com valor");
        return;
      }
      normalized = [...principals, ...defenses];
    } else {
      normalized = bookmakers
        .filter((b) => b.name.trim() && parseFloat(b.stake) > 0)
        .map((b) => ({ name: b.name.trim(), stake: parseFloat(b.stake), is_365: !!b.is_365 }));
      if (normalized.length < principalCount) {
        toast.error("Preencha todas as casas principais");
        return;
      }
    }
    if (!revenue || parseFloat(revenue) < 0) {
      toast.error("Informe a receita da operação");
      return;
    }

    setSaving(true);
    const payload = {
      date,
      operation_type: operationType,
      bookmakers: normalized,
      principal_count: Math.max(1, Math.min(principalCount, 5)),
      revenue: parseFloat(revenue),
      notes: notes || null,
      boost_percent: operationType === "aumentada" && boostPercent ? parseFloat(boostPercent) : null,
      freebet_value: isFreebetType && freebetValue ? parseFloat(freebetValue) : null,
      // Compose match if either team is provided
      match: (homeTeam.trim() && awayTeam.trim())
        ? `${homeTeam.trim()} x ${awayTeam.trim()}`
        : (match.trim() || null),
      home_team: homeTeam.trim() || null,
      away_team: awayTeam.trim() || null,
      early_payout: earlyPayout,
      pa_type: earlyPayout ? paType : null,
      weekly_365: weekly365,
      ticket_image_url: ticketImageUrl || null,
      freebet_sub_mode: isProcFreebet ? freebetSubMode : null,
      revenue_defense: isProcLucro && revenueDefense ? parseFloat(revenueDefense) : null,
      hit_side: null,
    };
    try {
      if (isEditing) {
        await api.put(`/entries/${editing.id}`, payload);
        toast.success("Entrada atualizada");
      } else {
        await api.post("/entries", payload);
        toast.success("Entrada registrada");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="entry-form-dialog"
        className="max-w-2xl border-white/10 bg-[#121212] p-0"
      >
        <DialogHeader className="border-b border-white/10 p-6 pb-4">
          <div className="flex items-center gap-3">
            {step === "form" && !isEditing && (
              <button
                data-testid="back-to-type-btn"
                onClick={() => setStep("type")}
                className="rounded-md p-1.5 text-zinc-400 hover:bg-white/5 hover:text-white"
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <div>
              <DialogTitle className="font-display text-xl font-bold text-white">
                {step === "type" ? "Nova operação" : isEditing ? "Editar operação" : "Preencher operação"}
              </DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                {step === "type"
                  ? "Escolha o tipo de entrada para começar"
                  : selectedType?.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === "type" ? (
          <div className="grid gap-3 p-6">
            <div
              data-testid="share-code-import"
              className="flex flex-col gap-2 rounded-xl border border-dashed border-[#D4FF00]/30 bg-[#D4FF00]/5 p-4 sm:flex-row sm:items-center"
            >
              <div className="flex items-center gap-2 text-sm text-zinc-300 sm:flex-1">
                <Link2 className="h-4 w-4 shrink-0 text-[#D4FF00]" />
                <span>Recebeu um <span className="font-semibold text-white">código de operação</span>? Cole aqui.</span>
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="share-code-input"
                  value={shareCode}
                  onChange={(e) => setShareCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && importShareCode()}
                  placeholder="EX: 7KQ2M9XA"
                  maxLength={12}
                  className="h-9 w-40 bg-black/40 font-mono-num uppercase tracking-widest"
                />
                <Button
                  data-testid="share-code-import-btn"
                  onClick={importShareCode}
                  disabled={!shareCode.trim() || importingCode}
                  className="h-9 bg-[#D4FF00] px-4 text-xs font-bold text-black hover:bg-[#c4ee00]"
                >
                  {importingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : "Importar"}
                </Button>
              </div>
            </div>
            {OPERATION_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`type-${t.key}-btn`}
                  onClick={() => {
                    setOperationType(t.key);
                    setStep("form");
                  }}
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-black/40 p-5 text-left hover:border-white/30"
                  style={{ transition: "border-color 0.2s ease, transform 0.2s ease" }}
                >
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${t.color}26`, border: `1px solid ${t.color}66` }}
                  >
                    <Icon className="h-6 w-6" style={{ color: t.color }} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-base font-bold text-white">{t.label}</div>
                    <div className="text-xs text-zinc-500">{t.description}</div>
                  </div>
                  <span className="text-zinc-600 group-hover:text-white">→</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="max-h-[70vh] space-y-5 overflow-y-auto p-6">
            {/* Type badge */}
            {selectedType && (
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium"
                  style={{
                    borderColor: `${selectedType.color}40`,
                    backgroundColor: `${selectedType.color}15`,
                    color: selectedType.color,
                  }}
                >
                  <selectedType.icon className="h-3 w-3" />
                  {selectedType.label}
                </span>
              </div>
            )}

            {/* Procedimento de Freebet sub-mode selector */}
            {isProcFreebet && (
              <div data-testid="freebet-submode-selector" className="rounded-xl border border-white/10 bg-black/30 p-4">
                <div className="mb-2 text-[0.7rem] uppercase tracking-widest text-zinc-500">Modalidade</div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      v: "guaranteed",
                      label: "Freebet Garantida",
                      desc: "Você recebe a freebet independentemente do resultado.",
                    },
                    {
                      v: "profit_guaranteed",
                      label: "Lucro Garantido",
                      desc: "Duas receitas: lucra se principal vencer OU se defesa bater.",
                    },
                  ].map((o) => {
                    const active = freebetSubMode === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        data-testid={`freebet-submode-${o.v}`}
                        onClick={() => setFreebetSubMode(o.v)}
                        className="flex flex-col items-start rounded-lg border px-3 py-2.5 text-left"
                        style={{
                          borderColor: active ? "#22D3EE" : "rgba(255,255,255,0.1)",
                          backgroundColor: active ? "#22D3EE18" : "transparent",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <span
                          className="text-xs font-bold"
                          style={{ color: active ? "#22D3EE" : "#D4D4D8" }}
                        >
                          {o.label}
                        </span>
                        <span className="mt-0.5 text-[0.7rem] leading-snug text-zinc-500">{o.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Date + optional type-specific */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                  Data da entrada
                </Label>
                <div className="mb-1.5 flex gap-1.5">
                  {(() => {
                    const t = todayBR();
                    const [y, m, d] = t.split("-").map(Number);
                    const tmr = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
                    const shortcuts = [
                      { v: t, label: "Hoje" },
                      { v: tmr, label: "Amanhã" },
                    ];
                    return shortcuts.map((s) => {
                      const active = date === s.v;
                      return (
                        <button
                          key={s.v}
                          type="button"
                          data-testid={`date-shortcut-${s.label.toLowerCase()}`}
                          onClick={() => setDate(s.v)}
                          className="h-7 rounded-full border px-3 text-[0.7rem] font-bold uppercase tracking-widest"
                          style={{
                            borderColor: active ? "#D4FF00" : "rgba(255,255,255,0.1)",
                            backgroundColor: active ? "#D4FF00" : "transparent",
                            color: active ? "#000" : "#D4D4D8",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {s.label}
                        </button>
                      );
                    });
                  })()}
                </div>
                <Input
                  data-testid="entry-date-input"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-black/40 font-mono-num"
                />
              </div>
              {operationType === "aumentada" && (
                <div>
                  <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                    Aumento (%)
                  </Label>
                  <Input
                    data-testid="boost-percent-input"
                    type="number"
                    step="0.01"
                    placeholder="Ex: 30"
                    value={boostPercent}
                    onChange={(e) => setBoostPercent(e.target.value)}
                    className="bg-black/40 font-mono-num"
                  />
                </div>
              )}
              {(operationType === "freebet" || isProcFreebet) && (
                <div>
                  <Label className="mb-1.5 flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#D4FF00]">
                    <Gift className="h-3 w-3" />
                    Valor de freebet na operação (R$)
                  </Label>
                  <Input
                    data-testid="freebet-value-input"
                    type="number"
                    step="0.01"
                    placeholder="Ex: 50"
                    value={freebetValue}
                    onChange={(e) => setFreebetValue(e.target.value)}
                    className="border-[#D4FF00]/30 bg-[#D4FF00]/5 font-mono-num focus-visible:ring-[#D4FF00]"
                  />
                  <p className="mt-1 text-[0.7rem] text-zinc-500">
                    {isProcFreebet
                      ? "Valor simbólico — indica que você recebe uma freebet. Não entra no cálculo do lucro."
                      : "Este valor será descontado do total investido no cálculo do lucro."}
                  </p>
                </div>
              )}
            </div>

            {/* Match / Confronto (Casa x Fora) — hidden for Giros */}
            {!isGiros && (
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                Confronto
              </Label>
              <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-2">
                <Input
                  data-testid="entry-home-team-input"
                  list="entry-team-options"
                  value={homeTeam}
                  onChange={(e) => setHomeTeam(e.target.value)}
                  placeholder="Casa"
                  className="bg-black/40"
                  autoComplete="off"
                />
                <span className="font-mono-num text-xs font-bold text-zinc-500">×</span>
                <Input
                  data-testid="entry-away-team-input"
                  list="entry-team-options"
                  value={awayTeam}
                  onChange={(e) => setAwayTeam(e.target.value)}
                  placeholder="Fora"
                  className="bg-black/40"
                  autoComplete="off"
                />
              </div>
              <datalist id="entry-team-options">
                {teamOptions.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              {matchOptions.length > 0 && (
                <div className="mt-2">
                  <div className="mb-1 text-[0.7rem] uppercase tracking-widest text-zinc-500">
                    Sugestões — confrontos em aberto
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(() => {
                      // Extra dedupe by "home|away" key
                      const seen = new Set();
                      const unique = [];
                      for (const m of matchOptions) {
                        const home = (m?.home || "").trim();
                        const away = (m?.away || "").trim();
                        if (!home || !away) continue;
                        const key = `${home.toLowerCase()}|${away.toLowerCase()}`;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        unique.push({ home, away });
                        if (unique.length >= 8) break;
                      }
                      if (unique.length === 0) {
                        return (
                          <span className="text-[0.75rem] text-zinc-600">
                            Nenhum confronto em aberto ainda.
                          </span>
                        );
                      }
                      return unique.map(({ home, away }) => {
                        const label = `${home} × ${away}`;
                        const active = homeTeam === home && awayTeam === away;
                        return (
                          <button
                            key={label}
                            type="button"
                            data-testid={`match-suggestion-${label}`}
                            onClick={() => {
                              setHomeTeam(home);
                              setAwayTeam(away);
                            }}
                            className="h-7 rounded-full border px-2.5 text-[0.75rem] font-medium"
                            style={{
                              borderColor: active ? "#D4FF00" : "rgba(255,255,255,0.1)",
                              backgroundColor: active ? "#D4FF0018" : "rgba(0,0,0,0.3)",
                              color: active ? "#D4FF00" : "#D4D4D8",
                              transition: "all 0.15s ease",
                            }}
                          >
                            {label}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Principal count picker — hidden for Giros */}
            {!isGiros && (
            <div className="flex items-center gap-3 rounded-lg border border-[#D4FF00]/20 bg-[#D4FF00]/5 p-3">
              <Label className="flex-1 text-xs uppercase tracking-widest text-[#D4FF00]">
                Casas principais
              </Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`principal-count-${n}`}
                    onClick={() => changePrincipalCount(n)}
                    className="h-8 w-8 rounded-md border text-xs font-bold"
                    style={{
                      borderColor: principalCount === n ? "#D4FF00" : "rgba(255,255,255,0.15)",
                      backgroundColor: principalCount === n ? "#D4FF00" : "transparent",
                      color: principalCount === n ? "#000" : "#D4D4D8",
                      transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Bookmakers */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">
                  {isGiros
                    ? "Casa do giro"
                    : `Casas · ${principalCount} principal${principalCount > 1 ? "is" : ""} + ${Math.max(0, bookmakers.length - principalCount)} defesa${bookmakers.length - principalCount === 1 ? "" : "s"}`}
                </Label>
                {!isGiros && (
                  <Button
                    data-testid="add-bookmaker-btn"
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addBookmaker}
                    disabled={bookmakers.length >= 10}
                    className="h-7 gap-1 text-[#D4FF00] hover:bg-[#D4FF00]/10 hover:text-[#D4FF00]"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Adicionar defesa
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {(isGiros ? bookmakers.slice(0, 1) : bookmakers).map((b, idx) => {
                  const isMain = idx < principalCount;
                  const mainIsFreebet = isMain && isExtracao;
                  const principalLabelIdx = idx + 1;
                  const defenseLabelIdx = idx - principalCount + 1;
                  return (
                    <div key={idx} className="grid grid-cols-[110px,1fr,140px,auto,auto] items-center gap-2">
                      <span
                        className="inline-flex h-9 items-center justify-center rounded-md border text-[0.7rem] font-bold uppercase tracking-widest"
                        style={
                          isGiros
                            ? { borderColor: "#F472B655", backgroundColor: "#F472B615", color: "#F472B6" }
                            : isMain
                            ? { borderColor: "#D4FF0055", backgroundColor: "#D4FF0015", color: "#D4FF00" }
                            : { borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(0,0,0,0.3)", color: "#71717A" }
                        }
                      >
                        {isGiros
                          ? "Casa"
                          : isMain
                          ? principalCount === 1
                            ? "Principal"
                            : `Principal ${principalLabelIdx}`
                          : `Defesa ${defenseLabelIdx}`}
                      </span>
                      <BankSelector
                        testId={`bookmaker-name-${idx}`}
                        banks={bankOptions}
                        value={b.name}
                        onChange={(v) => updateBookmaker(idx, "name", v)}
                        onBankCreated={(bank) => setBankOptions((prev) => [...prev, bank])}
                        placeholder={isGiros ? "Casa dos giros" : isMain ? "Casa da aposta principal" : "Casa da defesa"}
                      />
                      {isGiros ? (
                        <div
                          data-testid={`bookmaker-stake-${idx}`}
                          className="inline-flex h-9 items-center justify-center rounded-md border border-[#F472B6]/30 bg-[#F472B6]/10 px-2 text-[0.7rem] font-bold uppercase tracking-widest text-[#F472B6]"
                          title="Giros — sem stake em cash"
                        >
                          <Coins className="mr-1 h-3 w-3" /> Giros
                        </div>
                      ) : mainIsFreebet ? (
                        <div
                          data-testid={`bookmaker-stake-${idx}`}
                          className="inline-flex h-9 items-center justify-center rounded-md border border-[#D4FF00]/30 bg-[#D4FF00]/10 px-2 text-[0.7rem] font-bold uppercase tracking-widest text-[#D4FF00]"
                          title="Freebet — valor não conta como investimento"
                        >
                          <Gift className="mr-1 h-3 w-3" /> Freebet
                        </div>
                      ) : (
                        <Input
                          data-testid={`bookmaker-stake-${idx}`}
                          type="number"
                          step="0.01"
                          placeholder="Valor R$"
                          value={b.stake}
                          onChange={(e) => updateBookmaker(idx, "stake", e.target.value)}
                          className="bg-black/40 font-mono-num"
                        />
                      )}
                      <button
                        type="button"
                        data-testid={`bookmaker-is365-${idx}`}
                        onClick={() => updateBookmaker(idx, "is_365", !b.is_365)}
                        className="inline-flex h-9 min-w-[46px] items-center justify-center rounded-md border px-2 text-[0.7rem] font-bold uppercase tracking-widest"
                        style={{
                          borderColor: b.is_365 ? "#60A5FA" : "rgba(255,255,255,0.15)",
                          backgroundColor: b.is_365 ? "#60A5FA20" : "rgba(0,0,0,0.3)",
                          color: b.is_365 ? "#60A5FA" : "#71717A",
                          transition: "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
                          visibility: isGiros ? "hidden" : "visible",
                        }}
                        title="Incluir esta casa no Semanal 365"
                      >
                        365
                      </button>
                      {!isGiros ? (
                        <Button
                          data-testid={`remove-bookmaker-${idx}`}
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeBookmaker(idx)}
                          disabled={bookmakers.length === 1}
                          className="text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <div />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Revenue */}
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                {isGiros
                  ? "Valor ganho (R$)"
                  : isProcLucro
                  ? "Receita se principal vencer (R$)"
                  : "Receita total (R$)"}
              </Label>
              <Input
                data-testid="entry-revenue-input"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={revenue}
                onChange={(e) => setRevenue(e.target.value)}
                className="bg-black/40 font-mono-num text-lg"
              />
            </div>

            {isProcLucro && (
              <div>
                <Label className="mb-1.5 flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#22D3EE]">
                  <ShieldCheck className="h-3 w-3" />
                  Receita se defesa bater (R$)
                </Label>
                <Input
                  data-testid="entry-revenue-defense-input"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={revenueDefense}
                  onChange={(e) => setRevenueDefense(e.target.value)}
                  className="border-[#22D3EE]/30 bg-[#22D3EE]/5 font-mono-num text-lg focus-visible:ring-[#22D3EE]"
                />
                <p className="mt-1 text-[0.7rem] text-zinc-500">
                  Retorno recebido se as defesas baterem. A freebet é simbólica e não entra no cálculo.
                </p>
              </div>
            )}

            {/* Profit preview */}
            {isProcLucro ? (
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-white/10 bg-black/40 p-4">
                <div>
                  <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                    Se principal vencer
                  </div>
                  <div
                    className="font-mono-num text-base font-bold"
                    style={{ color: profitMainScenario >= 0 ? "#00FF66" : "#FF3B30" }}
                  >
                    {profitMainScenario >= 0 ? "+" : "-"}R$ {Math.abs(profitMainScenario).toFixed(2)}
                  </div>
                  <div className="mt-0.5 text-[0.7rem] text-zinc-500">
                    Receita − total investido
                  </div>
                </div>
                <div>
                  <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                    Se defesa bater
                  </div>
                  <div
                    data-testid="profit-preview"
                    className="font-mono-num text-base font-bold"
                    style={{ color: profitDefenseScenario >= 0 ? "#00FF66" : "#FF3B30" }}
                  >
                    {profitDefenseScenario >= 0 ? "+" : "-"}R$ {Math.abs(profitDefenseScenario).toFixed(2)}
                  </div>
                  <div className="mt-0.5 text-[0.7rem] text-zinc-500">
                    Receita def. − total investido
                  </div>
                </div>
                <div className="col-span-2 border-t border-white/5 pt-2">
                  <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                    Lucro mínimo garantido
                  </div>
                  <div
                    className="font-mono-num text-lg font-bold"
                    style={{ color: profit >= 0 ? "#D4FF00" : "#FF3B30" }}
                  >
                    {profit >= 0 ? "+" : "-"}R$ {Math.abs(profit).toFixed(2)}
                  </div>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-3 gap-2 rounded-lg border border-white/10 bg-black/40 p-4">
              <div>
                <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                  Investido{" "}
                  {isExtracao
                    ? "(apenas defesas)"
                    : isProcFreebet
                    ? "(total)"
                    : operationType === "freebet" && freebetFloat > 0
                    ? "(líquido)"
                    : ""}
                </div>
                <div className="font-mono-num text-base text-white">
                  R$ {effectiveStake.toFixed(2)}
                </div>
                {operationType === "freebet" && freebetFloat > 0 && (
                  <div className="mt-0.5 text-[0.7rem] text-zinc-500">
                    Bruto R$ {totalStake.toFixed(2)} − freebet R$ {freebetFloat.toFixed(2)}
                  </div>
                )}
                {isExtracao && (
                  <div className="mt-0.5 text-[0.7rem] text-zinc-500">
                    Principal é freebet — não conta como investimento
                  </div>
                )}
                {isProcFreebet && (
                  <div className="mt-0.5 text-[0.7rem] text-zinc-500">
                    Inclui principal + defesas
                  </div>
                )}
              </div>
              <div>
                <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">Receita</div>
                <div className="font-mono-num text-base text-white">
                  R$ {(parseFloat(revenue) || 0).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">Lucro</div>
                <div
                  data-testid="profit-preview"
                  className="font-mono-num text-base font-bold"
                  style={{ color: profit >= 0 ? "#00FF66" : "#FF3B30" }}
                >
                  {profit >= 0 ? "+" : "-"}R$ {Math.abs(profit).toFixed(2)}
                </div>
              </div>
            </div>
            )}

            {/* Early payout checkbox — hidden for Giros (não faz sentido) */}
            {!isGiros && (
            <div className="space-y-3 rounded-lg border border-white/10 bg-black/30 p-4">
              <label
                htmlFor="entry-early-payout"
                className="flex cursor-pointer items-start gap-3"
              >
                <Checkbox
                  id="entry-early-payout"
                  data-testid="entry-early-payout"
                  checked={earlyPayout}
                  onCheckedChange={(v) => setEarlyPayout(!!v)}
                  className="mt-0.5 border-white/30 data-[state=checked]:border-[#D4FF00] data-[state=checked]:bg-[#D4FF00] data-[state=checked]:text-black"
                />
                <div>
                  <div className="text-sm font-semibold text-white">Pagamento antecipado (PA)</div>
                  <div className="text-xs text-zinc-500">
                    Marque se esta operação foi encerrada com cash out / pagamento antecipado da casa.
                  </div>
                </div>
              </label>
              {earlyPayout && (
                <div className="ml-7 border-l border-[#D4FF00]/20 pl-4">
                  <div className="mb-2 text-[0.7rem] uppercase tracking-widest text-[#D4FF00]">
                    Tipo de PA
                  </div>
                  <RadioGroup
                    data-testid="pa-type-radio"
                    value={paType}
                    onValueChange={setPaType}
                    className="grid grid-cols-3 gap-2"
                  >
                    {[
                      { v: "ambos", label: "PA Ambos times" },
                      { v: "casa", label: "PA Casa" },
                      { v: "fora", label: "PA Fora" },
                    ].map((o) => (
                      <label
                        key={o.v}
                        htmlFor={`pa-${o.v}`}
                        className="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-black/40 px-3 py-2 text-xs text-zinc-300 hover:border-[#D4FF00]/40 has-[[data-state=checked]]:border-[#D4FF00] has-[[data-state=checked]]:bg-[#D4FF00]/10 has-[[data-state=checked]]:text-[#D4FF00]"
                        style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
                      >
                        <RadioGroupItem
                          id={`pa-${o.v}`}
                          value={o.v}
                          data-testid={`pa-type-${o.v}`}
                          className="border-white/40 text-[#D4FF00]"
                        />
                        {o.label}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>
            )}

            {/* Weekly 365 checkbox — hidden for Giros */}
            {!isGiros && (
            <label
              htmlFor="entry-weekly-365"
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-white/10 bg-black/30 p-4 hover:border-blue-400/40"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <Checkbox
                id="entry-weekly-365"
                data-testid="entry-weekly-365"
                checked={weekly365}
                onCheckedChange={(v) => setWeekly365(!!v)}
                className="mt-0.5 border-white/30 data-[state=checked]:border-blue-400 data-[state=checked]:bg-blue-400 data-[state=checked]:text-black"
              />
              <div>
                <div className="text-sm font-semibold text-white">Semanal 365</div>
                <div className="text-xs text-zinc-500">
                  Marque para incluir esta operação na aba de monitoramento semanal da Bet365.
                </div>
              </div>
            </label>
            )}

            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                Observações (opcional)
              </Label>
              <Textarea
                data-testid="entry-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex: Barcelona vs Real Madrid, over 2.5..."
                className="min-h-[70px] bg-black/40"
              />
            </div>

            {supportsTicketUpload && (
              <div data-testid="ticket-upload-section">
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                  Bilhete da aposta (opcional)
                </Label>
                {ticketImageUrl ? (
                  <div className="relative overflow-hidden rounded-lg border border-white/10 bg-black/40">
                    <TicketPreview url={ticketImageUrl} />
                    <button
                      type="button"
                      data-testid="ticket-remove-btn"
                      onClick={() => setTicketImageUrl(null)}
                      className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-zinc-300 backdrop-blur transition hover:bg-red-500/80 hover:text-white"
                      aria-label="Remover imagem"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    data-testid="ticket-upload-label"
                    className={`group flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-white/15 bg-black/30 px-4 py-6 text-center transition hover:border-[#D4FF00]/50 hover:bg-[#D4FF00]/5 ${
                      uploadingTicket ? "pointer-events-none opacity-60" : ""
                    }`}
                  >
                    {uploadingTicket ? (
                      <>
                        <Loader2 className="mb-1.5 h-6 w-6 animate-spin text-[#D4FF00]" />
                        <span className="text-xs font-semibold text-zinc-300">Enviando…</span>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="mb-1.5 h-6 w-6 text-zinc-500 group-hover:text-[#D4FF00]" strokeWidth={1.5} />
                        <span className="text-xs font-semibold text-zinc-300">
                          Anexar imagem do bilhete
                        </span>
                        <span className="mt-0.5 text-[0.7rem] text-zinc-500">JPG, PNG ou WebP · até 5MB</span>
                      </>
                    )}
                    <input
                      data-testid="ticket-upload-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => handleTicketFile(e.target.files?.[0])}
                      disabled={uploadingTicket}
                    />
                  </label>
                )}
              </div>
            )}
          </div>
        )}

        {step === "form" && (
          <DialogFooter className="border-t border-white/10 p-6 pt-4">
            <Button
              data-testid="cancel-entry-btn"
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-zinc-400 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              data-testid="save-entry-btn"
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#D4FF00] font-bold text-black hover:bg-[#c4ee00]"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {saving ? "Salvando..." : isEditing ? "Atualizar" : "Registrar entrada"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Ticket image preview (uses cookie auth) ---------- */
function TicketPreview({ url }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        // url is like "/api/files/<path>" — strip "/api" to match axios baseURL
        const relative = url.startsWith("/api") ? url.slice(4) : url;
        const res = await api.get(relative, { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (error) {
    return (
      <div className="flex h-40 items-center justify-center text-xs text-zinc-500">
        Não foi possível carregar a imagem
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
      </div>
    );
  }
  return (
    <img
      src={blobUrl}
      alt="Bilhete da aposta"
      data-testid="ticket-preview-img"
      className="max-h-80 w-full object-contain"
    />
  );
}
